import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import Avatar from "flarum/common/components/Avatar";
import humanTime from "flarum/common/helpers/humanTime";
import { SearchResultsSkeleton } from "./Skeletons";
import { messagePreview } from "../../common/utils/preview";
import { authorLink } from "../utils/bot";
/** Keystrokes are coalesced for this long before a request goes out. */
const DEBOUNCE = 300;
const PAGE_SIZE = 30;

/**
 * Message search, scoped to one channel or to everything the actor can read.
 *
 * Queries `filter[q]` on the message searcher, so results are already restricted
 * by the same visibility scope the stream uses — there is no separate permission
 * check to keep in step here.
 */
export default class ChatSearch extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "query", "");
    _defineProperty(this, "results", []);
    _defineProperty(this, "searching", false);
    /** Whether a query has run, to tell "no results" apart from "nothing typed". */
    _defineProperty(this, "searched", false);
    _defineProperty(this, "timer", null);
    /** Guards against an earlier, slower request overwriting a later one. */
    _defineProperty(this, "sequence", 0);
  }
  onremove() {
    if (this.timer !== null) window.clearTimeout(this.timer);
  }
  view() {
    const channel = this.attrs.channelId ? this.attrs.state.channel(this.attrs.channelId) : null;
    return m("div", {
      className: "ChatSearch"
    }, m("div", {
      className: "ChatSearch-bar"
    }, m("i", {
      className: "ChatSearch-icon fas fa-magnifying-glass",
      "aria-hidden": "true"
    }), m("input", {
      className: "ChatSearch-input",
      type: "search",
      value: this.query,
      placeholder: app.translator.trans("ramon-chat.forum.search.placeholder", {}, true),
      "aria-label": app.translator.trans("ramon-chat.forum.search.title", {}, true),
      oninput: e => this.onInput(e.target.value),
      onkeydown: e => {
        // Enter searches immediately rather than waiting out the debounce.
        if (e.key === "Enter") this.run();
      }
    }), this.query ? m(Button, {
      className: "Button Button--icon Button--flat ChatSearch-clear",
      icon: "fas fa-xmark",
      onclick: () => this.clear()
    }) : null), channel ? m("div", {
      className: "ChatSearch-scope"
    }, app.translator.trans("ramon-chat.forum.search.in_channel", {
      channel: channel.displayName()
    })) : null, this.body());
  }
  body() {
    if (this.searching && this.results.length === 0) {
      return SearchResultsSkeleton();
    }
    if (!this.searched) {
      return m("div", {
        className: "ChatBrowse-empty"
      }, app.translator.trans("ramon-chat.forum.search.prompt"));
    }
    if (this.results.length === 0) {
      return m("div", {
        className: "ChatBrowse-empty"
      }, app.translator.trans("ramon-chat.forum.search.empty"));
    }
    return m("div", {
      className: "ChatSearch-results"
    }, this.results.map(message => this.result(message)));
  }
  result(message) {
    const channel = this.attrs.state.channel(message.channelId());
    const at = message.createdAt();
    return m("button", {
      type: "button",
      className: "ChatSearch-result",
      key: message.id(),
      onclick: () => this.open(message)
    }, m(Avatar, {
      user: message.user(),
      className: "Avatar"
    }), m("div", {
      className: "ChatSearch-result-body"
    }, m("div", {
      className: "ChatSearch-result-meta"
    }, m("span", {
      className: "ChatSearch-result-author"
    }, authorLink(message)), channel ? m("span", {
      className: "ChatSearch-result-channel"
    }, channel.displayName()) : null, at ? m("span", {
      className: "ChatSearch-result-time"
    }, humanTime(at)) : null), m("div", {
      className: "ChatSearch-result-excerpt"
    }, messagePreview(message))));
  }

  // ── Behaviour ──────────────────────────────────────────────────────────────

  onInput(value) {
    this.query = value;
    if (this.timer !== null) window.clearTimeout(this.timer);
    if (value.trim() === "") {
      this.results = [];
      this.searched = false;
      return;
    }
    this.timer = window.setTimeout(() => this.run(), DEBOUNCE);
  }
  async run() {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    const term = this.query.trim();
    if (term === "") return;
    const mine = ++this.sequence;
    this.searching = true;
    m.redraw();
    try {
      const results = await app.store.find("chat-messages", {
        filter: _objectSpread({
          q: term
        }, this.attrs.channelId ? {
          channel: this.attrs.channelId
        } : {}),
        sort: "-id",
        page: {
          limit: PAGE_SIZE
        }
      });

      // A slower earlier request must not clobber a later one's results.
      if (mine !== this.sequence) return;
      this.results = Array.isArray(results) ? results : [];
      this.searched = true;
    } catch (_unused) {
      if (mine !== this.sequence) return;
      this.results = [];
      this.searched = true;
    } finally {
      if (mine === this.sequence) this.searching = false;
      m.redraw();
    }
  }
  clear() {
    this.query = "";
    this.results = [];
    this.searched = false;
    if (this.timer !== null) window.clearTimeout(this.timer);
    m.redraw();
  }

  /**
   * Opens the result's channel.
   *
   * It does not jump to the message itself: the stream pages backwards from the
   * newest message, so landing on an old one would mean loading everything in
   * between. Better to open the conversation than to appear to hang.
   */
  open(message) {
    const channelId = message.channelId();
    if (!channelId) return;
    this.attrs.state.setActiveChannel(channelId);
    m.route.set(app.route("chat.channel", {
      id: channelId
    }));
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChatSearch', ChatSearch);